import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Debt, DebtPayment, Organization } from '@nargus/shared';
import { Check, LoaderCircle, Paperclip, Upload, X } from 'lucide-react';
import { useAddPayment, useUpdatePayment } from '@/resources/debt/debt.service';
import { MAX_FILE_BYTES, formatSize } from '@/lib/files';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  ErrorNote,
  formatEuro,
  fromDateInput,
  toAmountInput,
  toCents,
  toDateInput,
} from './ui';

export function PaymentDialog({
  organization,
  debt,
  payment,
  suggestedCents,
  onClose,
}: {
  organization: Organization;
  debt: Debt;
  payment?: DebtPayment;
  suggestedCents?: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState(
    toAmountInput(payment?.amountCents ?? suggestedCents),
  );
  const [paidAt, setPaidAt] = useState(
    toDateInput(payment?.paidAt ?? new Date().toISOString()),
  );
  const [receipt, setReceipt] = useState<File | null>(null);
  const [tooLarge, setTooLarge] = useState(false);

  const add = useAddPayment();
  const update = useUpdatePayment();
  const mutation = payment ? update : add;

  const rateCents = debt.nextDueCents;
  const restCents = debt.remainingCents;

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    setTooLarge(Boolean(file && file.size > MAX_FILE_BYTES));
    setReceipt(file && file.size <= MAX_FILE_BYTES ? file : null);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const amountCents = toCents(amount);
    if (!Number.isFinite(amountCents) || amountCents < 1) return;

    const shared = {
      organizationId: organization.id,
      debtId: debt.id,
      amountCents,
      paidAt: fromDateInput(paidAt) ?? undefined,
      receipt: receipt ?? undefined,
    };

    if (payment) {
      update.mutate({ ...shared, paymentId: payment.id }, { onSuccess: onClose });
      return;
    }
    add.mutate(shared, { onSuccess: onClose });
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {payment ? t('debts.paymentEditTitle') : t('debts.paymentAddTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('debts.paymentDialogHint', { creditor: debt.creditor })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="payment-amount">{t('debts.paymentAmountLabel')}</FieldLabel>
              <Input
                id="payment-amount"
                type="number"
                required
                autoFocus
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
              {!payment && (
                <div className="flex flex-wrap gap-2">
                  {rateCents !== undefined && rateCents !== restCents && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAmount(toAmountInput(rateCents))}
                    >
                      {t('debts.paymentFillRate', { amount: formatEuro(rateCents) })}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAmount(toAmountInput(restCents))}
                  >
                    {t('debts.paymentFillRest', { amount: formatEuro(restCents) })}
                  </Button>
                </div>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="payment-date">{t('debts.paymentDateLabel')}</FieldLabel>
              <Input
                id="payment-date"
                type="date"
                required
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="payment-receipt">
                {t('debts.paymentReceiptLabel')}
              </FieldLabel>

              {receipt ? (
                <div className="flex items-center gap-2">
                  <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm">{receipt.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatSize(receipt.size)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0"
                    aria-label={t('debts.paymentReceiptClear')}
                    onClick={() => setReceipt(null)}
                  >
                    <X />
                  </Button>
                </div>
              ) : (
                payment?.receipt && (
                  <FieldDescription>
                    {t('debts.paymentReceiptCurrent', { filename: payment.receipt.filename })}
                  </FieldDescription>
                )
              )}

              <input
                id="payment-receipt"
                ref={picker}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={onPick}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => picker.current?.click()}
              >
                <Upload />
                {payment?.receipt
                  ? t('debts.paymentReceiptReplace')
                  : t('debts.paymentReceiptPick')}
              </Button>
              <FieldDescription>{t('debts.paymentReceiptHint')}</FieldDescription>
              {tooLarge && (
                <p className="text-sm text-destructive">{t('common.fileTooLarge')}</p>
              )}
            </Field>

            <ErrorNote error={mutation.error} />
          </FieldGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Check />
              )}
              {payment ? t('common.save') : t('debts.paymentSave')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
